import { SITE_DOMAIN } from '../../../constants';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@ui/components/Button/Button';
import { LungAnimation } from '@ui/components/LungAnimation/LungAnimation';
import { CircleAction } from '../../../domain/models';
import { useAuth } from '@breath-lab/state';
import { ROUTES } from '../../../routes';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent } from '../../../domain/analytics';
import styles from './HeroSection.module.css';

const PHASE_ACTIONS: { action: CircleAction; durationMs: number }[] = [
  { action: CircleAction.grow,      durationMs: 4000 },
  { action: CircleAction.holdLarge, durationMs: 4000 },
  { action: CircleAction.shrink,    durationMs: 4000 },
  { action: CircleAction.holdSmall, durationMs: 4000 },
];

export default function HeroSection() {
  const { t } = useTranslation('landing');
  const { openLoginModal, isAuthenticated } = useAuth();
  const [phaseIndex, setPhaseIndex] = useState(0);

  const PHASES = [
    { ...PHASE_ACTIONS[0], label: t('configurability.phases.inhale') },
    { ...PHASE_ACTIONS[1], label: t('configurability.phases.holdFull') },
    { ...PHASE_ACTIONS[2], label: t('configurability.phases.exhale') },
    { ...PHASE_ACTIONS[3], label: t('configurability.phases.holdEmpty') },
  ];
  const [countdown, setCountdown] = useState(PHASES[0].durationMs / 1000);

  useEffect(() => {
    const { durationMs } = PHASES[phaseIndex];
    setCountdown(durationMs / 1000);

    const tick = setInterval(() => {
      setCountdown((s) => (s > 1 ? s - 1 : s));
    }, 1000);
    const advance = setTimeout(() => setPhaseIndex((i) => (i + 1) % PHASES.length), durationMs);

    return () => { clearInterval(tick); clearTimeout(advance); };
  }, [phaseIndex]);

  const { action, label, durationMs } = PHASES[phaseIndex];

  return (
    <section className={styles.hero}>
      <div className={styles.inner}>
        <h1 className={styles.headline}>
          {t('hero.headline').split('\n').map((line, i, arr) => (
            i < arr.length - 1
              ? <span key={i}>{line}<br /></span>
              : <span key={i}>{line}</span>
          ))}
        </h1>
        <p className={styles.subheadline}>
          {t('hero.subheadline')}
        </p>
        {isAuthenticated ? (
          <Button as={Link} to={ROUTES.loggedInHome} onClick={() => logEvent(AnalyticsEvent.landingHeroCtaClick)}>
            {t('hero.ctaLoggedIn')}
          </Button>
        ) : (
          <Button onClick={() => { logEvent(AnalyticsEvent.landingHeroCtaClick); openLoginModal(); }}>
            {t('hero.cta')}
          </Button>
        )}

        <div className={styles.browserWrap} aria-hidden="true">
          <div className={styles.browserFrame}>
            <div className={styles.browserChrome}>
              <div className={styles.browserUrl}>{SITE_DOMAIN}</div>
            </div>
            <div className={styles.browserScreen}>
              <p className={styles.playerTechnique}>Sama Vritti</p>
              <div className={styles.playerTree}>
                <LungAnimation circleSize={1} durationMs={durationMs} phase={action} />
              </div>
              <div className={styles.playerFooter}>
                <p className={styles.playerPhaseLabel}>{label}</p>
                <p className={styles.playerCountdown}>{countdown}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
